import {
  $glimrTagsAtom,
  $ppidAtom,
  $pageViewIdAtom
} from '@client/core/atoms/config.js';
import { $gamTargetingAtom } from '@client/core/atoms/gamTargeting.js';
import { updatePlacementKeyValueById } from '@client/core/atoms/placements.js';
import { GridSizingHandler } from '@client/core/utils/dom/GridSizingHandler.js';
import { getGlimrTags } from '@client/core/utils/glimr.js';
import { isFinnTravelResultPage } from '@client/core/utils/travel/isFinnTravelResultPage.js';
import type { Event } from '@podium/browser';
import { events } from '@schibsted-nmp/advertising-events';
import {
  debugLog,
  DisplayPlacementId,
  type GamConfig
} from '@schibsted-nmp/advertising-shared';

import { Api } from './Api/index.js';
import * as EventListeners from './EventListeners/index.js';
import { LazyLoad } from './LazyLoad.js';
import { setTargetingOnSlotOrGlobal } from './targeting.js';

export async function initiateGam(
  gamConfig: GamConfig,
  ...abortSignals: ReadonlyArray<AbortSignal>
) {
  await EventListeners.setup(...abortSignals);

  const googletag = await Api(...abortSignals);

  googletag.pubads().enableSingleRequest();
  googletag.pubads().collapseEmptyDivs(true);
  googletag.pubads().disableInitialLoad();
  googletag.pubads().setCentering(true);

  if (!isFinnTravelResultPage()) {
    await LazyLoad.enable(...abortSignals);
  }

  GridSizingHandler.initiate(googletag);

  // Set global targeting
  $ppidAtom.subscribe(initiateGam.onPpidChange);
  $glimrTagsAtom.subscribe(initiateGam.onGlimrTagsChange);
  $pageViewIdAtom.subscribe(initiateGam.onPageViewIdChange);
  $gamTargetingAtom.subscribe(initiateGam.onGamTargetingChange);

  // Enable services now that everything is set up
  googletag.enableServices();

  debugLog('Google Ad Manager initialized', googletag, gamConfig);
}

export namespace initiateGam {
  export function onPpidChange({ delta }: $ppidAtom.Value) {
    if (!delta) return;

    const ppid = delta.ppId1 || delta.ppId2;
    if (!ppid) return;

    try {
      debugLog('Setting PPID on Gam', ppid);
      googletag.pubads().setPublisherProvidedId(ppid);
    } catch (error) {
      debugLog('Error setting PPID on GAM', error);
    }
  }

  export function onGlimrTagsChange(glimrTags: $glimrTagsAtom.Value) {
    const targeting = getGlimrTags(glimrTags);

    debugLog('Setting GLIMR targeting on GAM', targeting);

    setTargetingOnSlotOrGlobal({ targeting, global: true }).catch(
      (error: unknown) => {
        debugLog('Error setting GLIMR targeting on GAM', error);
      }
    );
  }

  export function onGamTargetingChange(targeting: $gamTargetingAtom.Value) {
    setTargetingOnSlotOrGlobal({ targeting, global: true }).catch(
      (error: unknown) => {
        debugLog('Error setting global targeting on GAM', error);
      }
    );
  }

  export function onPageViewIdChange(pageViewId: $pageViewIdAtom.Value) {
    if (!pageViewId) return;

    debugLog('Setting PageViewId on GAM', pageViewId);

    setTargetingOnSlotOrGlobal({
      targeting: [{ key: 'PageViewId', value: [pageViewId] }],
      global: true
    }).catch((error: unknown) => {
      debugLog('Error setting PageViewId on GAM', error);
    });
  }

  export function handleInCarouselImageUpdate(
    evt: Event<{ imageUri: string }>
  ) {
    const { imageUri } =
      events.ITEM_PAGE.INCAROUSEL_IMAGE_UPDATE.getPayload(evt);

    if (imageUri) {
      debugLog('INCAROUSEL_IMAGE_UPDATE received', imageUri);

      setTargetingOnSlotOrGlobal({
        targeting: [{ key: 'itemsecuredthumb', value: [imageUri] }],
        global: true
      }).catch((error: unknown) => {
        debugLog('Error setting itemsecuredthumb on GAM', error);
      });
    }

    // bit of a hack, but incarousel ad needs refreshing after reopening the gallery, this seems to be the less painfull way
    updatePlacementKeyValueById(
      DisplayPlacementId.AdvtInCarousel,
      'status',
      'refresh'
    );
  }
}
